import { apiFetch } from '../api/client'
import type { UnlockShareBody } from '../api/types.gen'
import type {
  BundleStatus,
  PortalSession,
  SelectionQuota,
  SharedAsset,
  SharedProject,
} from './types'

/**
 * Cliente del portal público.
 *
 * Autenticación distinta a la del estudio: `Share <jwt>` en lugar de `Bearer`,
 * y sin refresh token — un 401 aquí significa «vuelve a abrir el enlace», no
 * «renueva la sesión».
 */

function auth(sessionToken: string): { authorization: string } {
  return { authorization: `Share ${sessionToken}` }
}

/**
 * Cambia el token del enlace por una sesión.
 *
 * Es la ÚNICA llamada que lleva el token del enlace. A partir de aquí viaja el
 * JWT, para que el token permanente no acabe en el historial ni en cabeceras
 * Referer si el cliente comparte una captura o un enlace interno.
 */
export async function unlock(
  linkToken: string,
  password: string | null,
  visitorId: string | null,
): Promise<PortalSession> {
  const response = await apiFetch<{ data: PortalSession }, UnlockShareBody>(
    `/public/share/${linkToken}/unlock`,
    {
      method: 'POST',
      body: {
        password,
        // Recuperar SUS favoritos al volver: sin esto, cada visita sería una
        // persona nueva y el cliente perdería su selección.
        visitor_id: visitorId,
      },
      skipRefresh: true,
    },
  )

  return response.data
}

export async function fetchProject(sessionToken: string): Promise<SharedProject> {
  const response = await apiFetch<{ data: SharedProject }>('/public/share/project', auth(sessionToken))

  return response.data
}

export async function fetchAssets(
  sessionToken: string,
  options: { cursor?: string | null; limit?: number; onlySelected?: boolean } = {},
): Promise<{ data: SharedAsset[]; next_cursor: string | null; reads_as_list: boolean }> {
  const params = new URLSearchParams({ limit: String(options.limit ?? 40) })

  if (options.cursor) params.set('cursor', options.cursor)
  if (options.onlySelected) params.set('selected', '1')

  return apiFetch<{ data: SharedAsset[]; next_cursor: string | null; reads_as_list: boolean }>(
    `/public/share/assets?${params.toString()}`,
    auth(sessionToken),
  )
}

/**
 * Devuelven el cupo que queda, y hay que usarlo en vez de sumar uno.
 *
 * El cupo es de la ENTREGA, no de quien mira: marcar algo que la otra persona
 * ya había marcado no lo sube, y soltar algo ya descargado no lo baja. Sólo el
 * servidor sabe el número.
 */
export async function selectAsset(
  sessionToken: string,
  assetId: string,
  state: 'favorite' | 'rejected' | 'needs_edit' = 'favorite',
): Promise<SelectionQuota> {
  const res = await apiFetch<{ data: { selection_quota: SelectionQuota } }>(
    `/public/share/selections/${assetId}`,
    { method: 'PUT', body: { state }, ...auth(sessionToken) },
  )

  return res.data.selection_quota
}

export async function deselectAsset(
  sessionToken: string,
  assetId: string,
): Promise<SelectionQuota> {
  const res = await apiFetch<{ data: { selection_quota: SelectionQuota } }>(
    `/public/share/selections/${assetId}`,
    { method: 'DELETE', ...auth(sessionToken) },
  )

  return res.data.selection_quota
}

/** Por lotes: 40 fotos no deben costar 40 round-trips a US East. */
export async function requestDownloads(
  sessionToken: string,
  assetIds: string[],
): Promise<Array<{ asset_id: string; filename: string; url: string }>> {
  const response = await apiFetch<{
    data: Array<{ asset_id: string; filename: string; url: string }>
  }>('/public/share/downloads', {
    method: 'POST',
    body: { asset_ids: assetIds },
    ...auth(sessionToken),
  })

  return response.data
}

/**
 * Pide un ZIP con la selección.
 *
 * Asíncrono a propósito: comprimir 40 fotos de 25 MB es 1 GB que hay que bajar
 * de R2, empaquetar y volver a subir. Devuelve 202 y el cliente consulta el
 * estado.
 */
export async function requestBundle(
  sessionToken: string,
  assetIds: string[],
): Promise<{ bundle_id: string; status: string; file_count: number }> {
  const response = await apiFetch<{
    data: { bundle_id: string; status: string; file_count: number }
  }>('/public/share/bundles', {
    method: 'POST',
    body: { asset_ids: assetIds },
    ...auth(sessionToken),
  })

  return response.data
}

export async function fetchBundleStatus(
  sessionToken: string,
  bundleId: string,
): Promise<BundleStatus> {
  const response = await apiFetch<{ data: BundleStatus }>(
    `/public/share/bundles/${bundleId}`,
    auth(sessionToken),
  )

  return response.data
}
